// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { MembershipReportData } from '@/membership/api/membership-types';
import {
  MemberOnDay,
  SkuDelta,
  SkuSeries,
  SkuTotal,
  toDailyCountsBySku,
  toDailyDeltasBySku,
  toDayDate,
  toMembersOnDay,
  toSkuTotals,
} from '@/membership/utilities/membership-days';
import { toSkuColors } from '@/membership/utilities/sku-colors';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef, useState } from 'react';

// The chart opens on the whole project and the presets narrow it, because a
// window that opened part way through hid the rest of a lease behind a control
// nobody had a reason to touch.
/** One choice on the zoom control, as the toggle group renders it. */
export interface ZoomOption {
  value: string;
  label: string;
}

const ZOOM_PRESETS: ZoomOption[] = [
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
  { value: '365', label: '1y' },
];
export const ZOOM_ALL = 'all';

function toVisibleDays(zoom: string, dayCount: number): number {
  return zoom === ZOOM_ALL ? dayCount : Math.min(Number(zoom), dayCount);
}

// The serial list updates on the first move and then at most this often. A
// plain debounce left it blank for the whole delay, and a drag that never
// paused never showed anything at all.
const SCRUB_COOLDOWN_MS = 100;

export interface MembershipViewState {
  lastDayIndex: number;
  dayIndex: number;
  // The day the serial list answers. It trails dayIndex while the scrub bar is
  // moving, because walking every membership to list serials is the expensive
  // part. The figures above it read the live day and keep up.
  settledDayIndex: number;
  setDayIndex: (dayIndex: number) => void;
  dayDate: DateTime;
  endDate: DateTime;

  totals: SkuTotal[];
  colors: Record<string, string>;
  shownSkus: string[];
  isHidden: (itemSku: string) => boolean;
  toggleSku: (itemSku: string) => void;

  series: SkuSeries[];
  deltas: SkuDelta[];
  members: MemberOnDay[];

  // The window the chart draws, as an offset into the project and a length.
  // The scrub bar moves inside it rather than across the whole project.
  zoom: string;
  setZoom: (zoom: string) => void;
  zoomOptions: ZoomOption[];
  windowStart: number;
  visibleDays: number;
  windowStartOn: DateTime;
  canPageBack: boolean;
  canPageForward: boolean;
  pageBack: () => void;
  pageForward: () => void;
}

/**
 * The day the scrub bar sits on and the SKUs the chips leave showing, plus
 * everything derived from them.
 *
 * It lives outside the view because the edit dialog draws its serials in a
 * sidecar beside the whole dialog rather than inside the chart, and it cannot
 * read a day the view keeps to itself.
 */
export function useMembershipView(
  report: MembershipReportData | null
): MembershipViewState {
  const [chosenDay, setChosenDay] = useState<number | null>(null);
  const [chosenZoom, setChosenZoom] = useState<string | null>(null);
  const [chosenWindowStart, setChosenWindowStart] = useState<number | null>(
    null
  );
  // Hidden rather than shown, so a SKU the report gains later is covered
  // without anybody touching the chips.
  const [hiddenSkus, setHiddenSkus] = useState<string[]>([]);

  const lastDayIndex = Math.max((report?.dayCount ?? 1) - 1, 0);
  // The window grows by a day each night, so a day chosen before that is still
  // in range while an unset one follows the end of the window.
  const dayIndex = Math.min(chosenDay ?? lastDayIndex, lastDayIndex);

  const totals = useMemo(
    () => (report === null ? [] : toSkuTotals(report)),
    [report]
  );

  // Both inputs hold their identity between renders, so the memo does too. A
  // SKU is free text and may hold a space, which is why this is not derived
  // from a joined string.
  const shownSkus = useMemo(
    () =>
      totals
        .map((total) => total.itemSku)
        .filter((itemSku) => !hiddenSkus.includes(itemSku)),
    [totals, hiddenSkus]
  );

  const colors = useMemo(
    () => toSkuColors(totals.map((total) => total.itemSku)),
    [totals]
  );

  const series = useMemo(
    () => (report === null ? [] : toDailyCountsBySku(report, shownSkus)),
    [report, shownSkus]
  );
  const deltas = useMemo(
    () => (report === null ? [] : toDailyDeltasBySku(report, shownSkus)),
    [report, shownSkus]
  );
  const dayCount = report?.dayCount ?? 1;
  const zoomOptions = ZOOM_PRESETS.filter(
    (preset) => Number(preset.value) < dayCount
  ).concat({ value: ZOOM_ALL, label: 'All' });
  // A report that arrives shorter can drop the preset that was chosen, which
  // would leave the toggle group on a value it no longer offers.
  const hasChosenZoom = zoomOptions.some((one) => one.value === chosenZoom);
  const zoom = hasChosenZoom && chosenZoom !== null ? chosenZoom : ZOOM_ALL;
  const visibleDays = toVisibleDays(zoom, dayCount);
  const lastWindowStart = Math.max(dayCount - visibleDays, 0);
  // An unset window sits at the end of the project, which is where the scrub
  // bar starts and the only part of a long lease anyone opens on.
  const windowStart = Math.min(
    Math.max(chosenWindowStart ?? lastWindowStart, 0),
    lastWindowStart
  );

  const shownDay = Math.min(
    Math.max(dayIndex, windowStart),
    windowStart + visibleDays - 1
  );
  const [settledDayIndex, setSettledDayIndex] = useState(shownDay);
  const firedAtRef = useRef(0);
  useEffect(() => {
    const waited = Date.now() - firedAtRef.current;
    if (waited >= SCRUB_COOLDOWN_MS) {
      firedAtRef.current = Date.now();
      setSettledDayIndex(shownDay);
      return undefined;
    }
    // Still cooling down, so the move lands at the end of it. The cleanup drops
    // a superseded one, which is what keeps the last position from being lost.
    const timer = setTimeout(() => {
      firedAtRef.current = Date.now();
      setSettledDayIndex(shownDay);
    }, SCRUB_COOLDOWN_MS - waited);
    return () => {
      clearTimeout(timer);
    };
  }, [shownDay]);

  // A report that arrives shorter than the last one leaves the settled day past
  // its end, where nothing is a member and the list reads as empty.
  const listedDay = Math.min(
    Math.max(settledDayIndex, windowStart),
    windowStart + visibleDays - 1
  );
  const members = useMemo(
    () => (report === null ? [] : toMembersOnDay(report, listedDay, shownSkus)),
    [report, listedDay, shownSkus]
  );

  // Both windows are memoised because the charts skip re-rendering hundreds of
  // bars only while their data keeps the same identity. A fresh array every
  // render would defeat that on every scrub and every mouse move.
  const windowEnd = windowStart + visibleDays;
  const windowSeries = useMemo(
    () =>
      series.map((one) => ({
        itemSku: one.itemSku,
        counts: one.counts.slice(windowStart, windowEnd),
      })),
    [series, windowStart, windowEnd]
  );
  const windowDeltas = useMemo(
    () =>
      deltas.map((one) => ({
        itemSku: one.itemSku,
        joined: one.joined.slice(windowStart, windowEnd),
        left: one.left.slice(windowStart, windowEnd),
      })),
    [deltas, windowStart, windowEnd]
  );

  const startOn = report?.startOn ?? DateTime.now();
  return {
    lastDayIndex,
    dayIndex: shownDay,
    settledDayIndex: listedDay,
    setDayIndex: setChosenDay,
    dayDate: toDayDate(startOn, shownDay),
    endDate: toDayDate(startOn, lastDayIndex),

    zoom,
    setZoom: (next: string) => {
      // The day being read stays on screen, centred in the narrower window or
      // clamped at whichever end of the project it is near. Dropping the window
      // instead sent every zoom back to the end of the lease.
      const nextVisible = toVisibleDays(next, dayCount);
      setChosenZoom(next);
      setChosenWindowStart(
        Math.min(
          Math.max(shownDay - Math.floor(nextVisible / 2), 0),
          Math.max(dayCount - nextVisible, 0)
        )
      );
    },
    zoomOptions,
    windowStart,
    visibleDays,
    windowStartOn: toDayDate(startOn, windowStart),
    canPageBack: windowStart > 0,
    canPageForward: windowStart < lastWindowStart,
    pageBack: () => {
      setChosenWindowStart(Math.max(windowStart - visibleDays, 0));
    },
    pageForward: () => {
      setChosenWindowStart(
        Math.min(windowStart + visibleDays, lastWindowStart)
      );
    },

    totals,
    colors,
    shownSkus,
    isHidden: (itemSku: string) => hiddenSkus.includes(itemSku),
    toggleSku: (itemSku: string) => {
      setHiddenSkus((hidden) =>
        hidden.includes(itemSku)
          ? hidden.filter((one) => one !== itemSku)
          : [...hidden, itemSku]
      );
    },

    series: windowSeries,
    deltas: windowDeltas,
    members,
  };
}
